import {language} from '@/shared/localize/moment.js';
import locales from '@/shared/localize/locales.js';
import common from '@/shared/localize/common.js';
import IntlMessageFormat from 'intl-messageformat';

declare global {
    interface Window {
        IntlMessageFormat: typeof IntlMessageFormat;
    }
}

window.IntlMessageFormat = IntlMessageFormat;

interface LocalizationCache {
    messages: {[key: string]: IntlMessageFormat};
}

const __localizationCache: LocalizationCache = {messages: {}};

// Cache flattened common translations per language
const __commonCache: Record<string, Record<string, string>> = {};

function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
    const result: Record<string, string> = {};

    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            const newKey = prefix ? `${prefix}.${key}` : key;

            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                Object.assign(result, flattenObject(value as Record<string, unknown>, newKey));
            } else {
                result[newKey] = value as string;
            }
        }
    }

    return result;
}

export default function localize(key: string, ...rest: unknown[]) {
    const languageKey = language as keyof typeof locales;

    // Get or create flattened common translations cache for this language
    if (!__commonCache[languageKey]) {
        __commonCache[languageKey] = flattenObject(common[languageKey] || {});
    }
    const commonTranslations = __commonCache[languageKey];
    const translations = locales[languageKey];

    // First try to find in common resources
    let translatedValue: string | undefined = commonTranslations[key];

    // Fall back to locales if not found in common
    if (!translatedValue) {
        const localesValue = translations[key as keyof typeof translations];
        translatedValue =
            typeof localesValue === 'string' ? localesValue : JSON.stringify(localesValue);
    }

    if (!translatedValue) {
        console.warn(`Missing translation for key ${key}`);
    }

    // Fall back to the key itself if no translation found
    const safeValue = translatedValue || key;
    const messageKey = key + safeValue;
    let translatedMessage = __localizationCache.messages[messageKey];

    if (!translatedMessage) {
        translatedMessage = new IntlMessageFormat(String(safeValue), language, {});
        __localizationCache.messages[messageKey] = translatedMessage;
    }

    const args: Record<string, unknown> = {};

    for (let i = 0; i < rest.length; i += 2) {
        args[rest[i] as string] = rest[i + 1];
    }

    return translatedMessage.format(args);
}

/**
 * Creates a localize function that first checks local resources before falling back to global resources
 * @param localResources - Local translation resources with same structure as global locales
 * @returns A localize function that uses local resources as priority
 */
export function createLocalizeFunction(localResources: Partial<typeof locales>) {
    // Cache flattened translations per language to avoid repeated flattening
    const flattenedCache: Record<string, Record<string, string>> = {};

    return function localizeWithLocalResources(key: string, ...rest: unknown[]) {
        const languageKey = language as keyof typeof locales;

        // Use cached flattened translations or compute once per language
        if (!flattenedCache[languageKey]) {
            flattenedCache[languageKey] = flattenObject(localResources[languageKey] || {});
        }
        const localTranslations = flattenedCache[languageKey];

        // First try to find in local resources
        let translatedValue: string | undefined =
            localTranslations?.[key as keyof typeof localTranslations];

        // Fall back to the main localize function if not found locally
        // This ensures common resources and global locales are properly checked
        if (!translatedValue) {
            return localize(key, ...rest);
        }

        // If found in local resources, format it
        const safeValue = translatedValue;
        const messageKey = key + safeValue;
        let translatedMessage = __localizationCache.messages[messageKey];

        if (!translatedMessage) {
            translatedMessage = new IntlMessageFormat(String(safeValue), language, {});
            __localizationCache.messages[messageKey] = translatedMessage;
        }

        const args: Record<string, unknown> = {};

        for (let i = 0; i < rest.length; i += 2) {
            args[rest[i] as string] = rest[i + 1];
        }

        return translatedMessage.format(args);
    };
}
